import { useMemo } from "react";

import { Card } from "@component/card";
import BillingHiringActivity from "@container/billing-hiring-activity";
import BillingTrainingActivity from "@container/billing-training-activity";
import CurrentBillingPeriodCard from "@container/current-billing-period-card";
import { useUserProfileContext } from "@context/user_profile";
import { BillingPlatform, resolveBillingPlatform } from "@util/billing-platform";

/**
 * Shared "Workspace Activity" surface — platform-aware usage / activity levels.
 *
 * Rendered on BOTH the Subscription page ("Workspace Activity" tab) and the
 * Billing menu ("Workspace Activity" tab) so the two never drift:
 *  - recruiter: the real Billing "Hiring Activity" (Activity Summary + Activity
 *    Tracking), including its Current Billing Period card (`showBillingPeriod`).
 *  - training:  the dedicated training-activity API (roleplay / campaign / AIQM).
 *  - agent / operator / unknown: a simple "coming soon" panel.
 *
 * `showBillingPeriod` defaults to true (Billing menu — the period lives here).
 * The Subscription page passes `false` because its Payment Details tab already
 * renders the current-subscription context, so it would otherwise duplicate.
 *
 * Resolves the platform from the user profile; pass `platform` to override when
 * the parent already resolved it (e.g. the Subscription page).
 */
export default function BillingWorkspaceActivity({
    platform: platformProp,
    showBillingPeriod = true,
}: {
    platform?: BillingPlatform | null;
    showBillingPeriod?: boolean;
}) {
    const [, userInfo] = useUserProfileContext();
    const resolved = useMemo(() => resolveBillingPlatform(userInfo), [userInfo]);
    const platform = platformProp ?? resolved;

    if (platform === "recruiter") {
        return <BillingHiringActivity showBillingPeriod={showBillingPeriod} />;
    }
    if (platform === "training") {
        // BillingTrainingActivity has no billing-period card of its own, so render
        // the shared one above it (same gate as recruiter).
        return (
            <div className="space-y-6">
                {showBillingPeriod && <CurrentBillingPeriodCard />}
                <BillingTrainingActivity />
            </div>
        );
    }
    return <WorkspaceActivityComingSoon />;
}

function WorkspaceActivityComingSoon() {
    return (
        <Card className="p-10 text-center">
            <p className="text-sm text-gray-500">
                Workspace activity for this platform is coming soon.
            </p>
        </Card>
    );
}
